import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { surveyApi, departmentApi } from '../../services/api';
import {
  PageHeader, StatCard, EmptyState, Spinner, Modal, ConfirmModal,
} from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  PlusIcon, RocketLaunchIcon, ChartBarIcon, TrashIcon, PencilSquareIcon,
  ArrowUpIcon, ArrowDownIcon, XMarkIcon, ClipboardDocumentListIcon,
  UsersIcon, CheckBadgeIcon, SparklesIcon,
} from '@heroicons/react/24/outline';

const SURVEY_TYPES = [
  { value: 'annual_engagement',     label: 'Annual engagement' },
  { value: 'pulse',                 label: 'Pulse' },
  { value: 'enps',                  label: 'eNPS' },
  { value: 'onboarding',            label: 'Onboarding' },
  { value: 'exit',                  label: 'Exit' },
  { value: 'manager_effectiveness', label: 'Manager effectiveness' },
  { value: 'dei',                   label: 'DEI' },
  { value: 'custom',                label: 'Custom' },
];

const TYPE_COLORS = {
  annual_engagement:     'bg-blue-50 text-blue-700 border-blue-200',
  pulse:                 'bg-purple-50 text-purple-700 border-purple-200',
  enps:                  'bg-emerald-50 text-emerald-700 border-emerald-200',
  onboarding:            'bg-teal-50 text-teal-700 border-teal-200',
  exit:                  'bg-orange-50 text-orange-700 border-orange-200',
  manager_effectiveness: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  dei:                   'bg-pink-50 text-pink-700 border-pink-200',
  custom:                'bg-gray-100 text-gray-700 border-gray-200',
};

const STATUS_STYLES = {
  draft:  'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-800',
  closed: 'bg-slate-200 text-slate-700',
};

function typeLabel(t) {
  return SURVEY_TYPES.find(s => s.value === t)?.label || t;
}

const EMPTY_QUESTION = (i) => ({
  id: `q_${i + 1}`,
  type: 'rating',
  label: '',
  required: true,
  scale: 5,
  options: [],
});

function QuestionEditor({ q, index, total, onChange, onRemove, onMove }) {
  const setField = (k, v) => onChange({ ...q, [k]: v });
  const [optDraft, setOptDraft] = useState('');

  const addOption = () => {
    const v = optDraft.trim();
    if (!v) return;
    setField('options', [...(q.options || []), v]);
    setOptDraft('');
  };

  const removeOption = (idx) => {
    setField('options', q.options.filter((_, i) => i !== idx));
  };

  return (
    <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/50">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-500">
          Question {index + 1} · <span className="font-mono">{q.id}</span>
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={index === 0}
            onClick={() => onMove(index, index - 1)}
            className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
            title="Move up"
          >
            <ArrowUpIcon className="w-4 h-4 text-gray-600" />
          </button>
          <button
            type="button"
            disabled={index === total - 1}
            onClick={() => onMove(index, index + 1)}
            className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
            title="Move down"
          >
            <ArrowDownIcon className="w-4 h-4 text-gray-600" />
          </button>
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="p-1 rounded hover:bg-red-100"
            title="Remove"
          >
            <TrashIcon className="w-4 h-4 text-red-500" />
          </button>
        </div>
      </div>

      <label className="label">Prompt</label>
      <textarea
        className="input h-20 resize-none"
        value={q.label}
        onChange={e => setField('label', e.target.value)}
        placeholder="e.g. How satisfied are you with your manager's feedback cadence?"
      />

      <div className="grid grid-cols-2 gap-3 mt-3">
        <div>
          <label className="label">Type</label>
          <select
            className="input"
            value={q.type}
            onChange={e => setField('type', e.target.value)}
          >
            <option value="rating">Rating scale</option>
            <option value="text">Open text</option>
            <option value="multi_select">Multi-select</option>
            <option value="yes_no">Yes / No</option>
          </select>
        </div>
        {q.type === 'rating' && (
          <div>
            <label className="label">Scale (max)</label>
            <input
              type="number"
              min={2}
              max={10}
              className="input"
              value={q.scale || 5}
              onChange={e => setField('scale', parseInt(e.target.value, 10) || 5)}
            />
          </div>
        )}
        {q.type !== 'rating' && (
          <label className="flex items-center gap-2 mt-6 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={!!q.required}
              onChange={e => setField('required', e.target.checked)}
            />
            Required
          </label>
        )}
      </div>

      {q.type === 'rating' && (
        <label className="flex items-center gap-2 mt-3 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!q.required}
            onChange={e => setField('required', e.target.checked)}
          />
          Required
        </label>
      )}

      {q.type === 'multi_select' && (
        <div className="mt-3">
          <label className="label">Options</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {(q.options || []).map((opt, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full"
              >
                {opt}
                <button
                  type="button"
                  onClick={() => removeOption(i)}
                  className="p-0.5 hover:bg-blue-200 rounded-full"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </span>
            ))}
            {(!q.options || q.options.length === 0) && (
              <span className="text-xs text-gray-400 italic">No options yet</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              className="input flex-1"
              placeholder="Add option and press Enter"
              value={optDraft}
              onChange={e => setOptDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); addOption(); }
              }}
            />
            <button type="button" onClick={addOption} className="btn-secondary">Add</button>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateSurveyForm({ departments, onClose, onSubmit, submitting }) {
  const [name, setName]               = useState('');
  const [type, setType]               = useState('pulse');
  const [isAnon, setIsAnon]           = useState(true);
  const [targetDepts, setTargetDepts] = useState([]);
  const [startDate, setStartDate]     = useState('');
  const [endDate, setEndDate]         = useState('');
  const [questions, setQuestions]     = useState([EMPTY_QUESTION(0)]);

  const toggleDept = (id) => {
    setTargetDepts(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  };

  const addQuestion = () => {
    setQuestions(p => [...p, EMPTY_QUESTION(p.length)]);
  };

  const updateQuestion = (idx, q) => {
    setQuestions(p => p.map((x, i) => i === idx ? q : x));
  };

  const removeQuestion = (idx) => {
    setQuestions(p => p.filter((_, i) => i !== idx));
  };

  const moveQuestion = (from, to) => {
    setQuestions(p => {
      const next = [...p];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const valid =
    name.trim().length > 2 &&
    questions.length > 0 &&
    questions.every(q => q.label.trim().length > 0) &&
    questions.every(q => q.type !== 'multi_select' || (q.options && q.options.length >= 2));

  const handleSave = () => {
    if (!valid) return;
    onSubmit({
      name: name.trim(),
      type,
      is_anonymous: isAnon,
      target_departments: targetDepts,
      start_date: startDate || null,
      end_date:   endDate || null,
      questions: questions.map((q, i) => ({
        id:       q.id || `q_${i + 1}`,
        type:     q.type,
        label:    q.label.trim(),
        required: !!q.required,
        ...(q.type === 'rating'       ? { scale: q.scale || 5 } : {}),
        ...(q.type === 'multi_select' ? { options: q.options || [] } : {}),
      })),
    });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="label">Survey name</label>
          <input
            className="input"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Q2 Pulse Check"
          />
        </div>
        <div>
          <label className="label">Type</label>
          <select className="input" value={type} onChange={e => setType(e.target.value)}>
            {SURVEY_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 mt-6">
          <input
            id="anon"
            type="checkbox"
            checked={isAnon}
            onChange={e => setIsAnon(e.target.checked)}
          />
          <label htmlFor="anon" className="text-sm text-gray-700">
            Anonymous responses
          </label>
        </div>
        <div>
          <label className="label">Start date</label>
          <input
            type="date"
            className="input"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
        </div>
        <div>
          <label className="label">End date</label>
          <input
            type="date"
            className="input"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
          />
        </div>
      </div>

      <div>
        <label className="label">Target departments</label>
        {departments && departments.length > 0 ? (
          <div className="flex flex-wrap gap-2 p-2 border border-gray-200 rounded-lg max-h-32 overflow-y-auto">
            {departments.map(d => {
              const on = targetDepts.includes(d.id);
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => toggleDept(d.id)}
                  className={clsx(
                    'px-3 py-1 text-xs rounded-full border transition-colors',
                    on
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  )}
                >
                  {d.name}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-gray-400">No departments available — survey will target all employees.</p>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="section-title">Questions ({questions.length})</h4>
          <button type="button" onClick={addQuestion} className="btn-secondary text-xs">
            <PlusIcon className="w-4 h-4" /> Add question
          </button>
        </div>
        <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
          {questions.map((q, i) => (
            <QuestionEditor
              key={i}
              q={q}
              index={i}
              total={questions.length}
              onChange={(next) => updateQuestion(i, next)}
              onRemove={removeQuestion}
              onMove={moveQuestion}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button
          type="button"
          disabled={!valid || submitting}
          onClick={handleSave}
          className="btn-primary disabled:opacity-40"
        >
          {submitting ? 'Saving…' : 'Create survey'}
        </button>
      </div>
    </div>
  );
}

export default function SurveyDashboard() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const { data: surveysResp, isLoading } = useQuery({
    queryKey: ['surveys'],
    queryFn:  () => surveyApi.list().then(r => r.data),
  });

  const { data: departments } = useQuery({
    queryKey: ['departments'],
    queryFn:  () => departmentApi.list().then(r => r.data),
  });

  const createMut = useMutation({
    mutationFn: (data) => surveyApi.create(data),
    onSuccess:  () => {
      toast.success('Survey created');
      qc.invalidateQueries(['surveys']);
      setCreateOpen(false);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to create survey'),
  });

  const launchMut = useMutation({
    mutationFn: (id) => surveyApi.launch(id),
    onSuccess:  () => { toast.success('Survey launched'); qc.invalidateQueries(['surveys']); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Launch failed'),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => surveyApi.delete(id),
    onSuccess:  () => { toast.success('Survey deleted'); qc.invalidateQueries(['surveys']); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Delete failed'),
  });

  const surveys = surveysResp?.data ?? [];

  const stats = useMemo(() => {
    const active = surveys.filter(s => s.status === 'active').length;
    const thirtyAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const recentResponses = surveys.reduce((sum, s) => {
      const d = s.updated_at ? new Date(s.updated_at).getTime() : 0;
      return sum + (d >= thirtyAgo ? (s.response_count || 0) : 0);
    }, 0);
    const completion = surveys.length
      ? Math.round(
          (surveys.reduce((s, x) => s + (x.completion_rate ?? 0), 0) / surveys.length) * 100
        ) / 100
      : 0;
    const avgScore = (() => {
      const scored = surveys.filter(s => typeof s.avg_score === 'number');
      if (!scored.length) return null;
      return (scored.reduce((s, x) => s + x.avg_score, 0) / scored.length).toFixed(2);
    })();
    return { active, recentResponses, completion, avgScore };
  }, [surveys]);

  if (isLoading) {
    return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Surveys"
        subtitle="Measure engagement, capture feedback, and act on team sentiment."
        actions={
          <button onClick={() => setCreateOpen(true)} className="btn-primary">
            <PlusIcon className="w-4 h-4" /> Create survey
          </button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          title="Active surveys"
          value={stats.active}
          subtitle="Currently collecting responses"
          color="green"
          icon={RocketLaunchIcon}
        />
        <StatCard
          title="Responses (30d)"
          value={stats.recentResponses}
          subtitle="Across all surveys"
          color="blue"
          icon={UsersIcon}
        />
        <StatCard
          title="Avg completion"
          value={`${Math.round((stats.completion || 0) * 100)}%`}
          subtitle="Target reached"
          color="purple"
          icon={CheckBadgeIcon}
        />
        <StatCard
          title="Avg score"
          value={stats.avgScore ?? '—'}
          subtitle="Across rating questions"
          color="amber"
          icon={SparklesIcon}
        />
      </div>

      {surveys.length === 0 ? (
        <EmptyState
          title="No surveys yet"
          description="Launch a pulse or engagement survey to start collecting feedback from your team."
          action={
            <button onClick={() => setCreateOpen(true)} className="btn-primary">
              <PlusIcon className="w-4 h-4" /> Create your first survey
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {surveys.map(s => {
            const canLaunch  = s.status === 'draft';
            const canRespond = s.status === 'active';
            return (
              <div key={s.id} className="card p-5 flex flex-col">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-base font-semibold text-gray-900 leading-tight">{s.name}</h3>
                  <span className={clsx(
                    'text-xs font-medium px-2 py-0.5 rounded-full capitalize shrink-0 ml-2',
                    STATUS_STYLES[s.status] || STATUS_STYLES.draft
                  )}>
                    {s.status}
                  </span>
                </div>
                <span className={clsx(
                  'inline-flex w-fit text-xs px-2 py-0.5 rounded border mb-3',
                  TYPE_COLORS[s.type] || TYPE_COLORS.custom
                )}>
                  {typeLabel(s.type)}
                </span>

                <div className="grid grid-cols-2 gap-3 text-xs text-gray-600 mb-4">
                  <div>
                    <p className="text-gray-400">Responses</p>
                    <p className="font-semibold text-gray-800">
                      {s.response_count ?? 0}
                      {s.target_count ? ` / ${s.target_count}` : ''}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Window</p>
                    <p className="font-semibold text-gray-800">
                      {s.start_date ? new Date(s.start_date).toLocaleDateString() : '—'}
                      {' → '}
                      {s.end_date ? new Date(s.end_date).toLocaleDateString() : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Questions</p>
                    <p className="font-semibold text-gray-800">{s.questions?.length ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Visibility</p>
                    <p className="font-semibold text-gray-800">
                      {s.is_anonymous ? 'Anonymous' : 'Identified'}
                    </p>
                  </div>
                </div>

                <div className="mt-auto flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                  {canLaunch && (
                    <button
                      onClick={() => launchMut.mutate(s.id)}
                      disabled={launchMut.isPending}
                      className="btn-primary text-xs"
                    >
                      <RocketLaunchIcon className="w-4 h-4" /> Launch
                    </button>
                  )}
                  {canRespond && (
                    <Link to={`/app/surveys/${s.id}/respond`} className="btn-secondary text-xs">
                      <PencilSquareIcon className="w-4 h-4" /> Respond
                    </Link>
                  )}
                  <Link to={`/app/surveys/${s.id}/results`} className="btn-secondary text-xs">
                    <ChartBarIcon className="w-4 h-4" /> Results
                  </Link>
                  <button
                    onClick={() => setConfirmDelete(s)}
                    className="btn-danger text-xs ml-auto"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create survey"
        size="lg"
      >
        <CreateSurveyForm
          departments={departments}
          onClose={() => setCreateOpen(false)}
          onSubmit={(data) => createMut.mutate(data)}
          submitting={createMut.isPending}
        />
      </Modal>

      <ConfirmModal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => deleteMut.mutate(confirmDelete.id)}
        title="Delete survey?"
        message={`"${confirmDelete?.name}" and all its responses will be permanently removed. This cannot be undone.`}
        danger
      />
    </div>
  );
}
